// 119L: a "Publish" that jammed during a class left almost no trace — Workers Logs
// keep 5% of requests and Supabase's logs a day, and a request the browser gave up
// on at 20 s is recorded nowhere. Every slow, timed-out or failed API call is now
// written to D1 `slow_requests` by the browser and by the Worker. Pure and small:
// src/lib/api.ts sits in the entry bundle and imports this.

/** An API call at least this slow is written down. */
export const SLOW_REQUEST_MS = 5_000;
export const SLOW_REQUEST_DAYS = 30;
/** Where the browser sends its reports, under /api. Never reported on itself. */
export const SLOW_REPORT_PATH = "/telemetry/slow";

export const SLOW_KINDS = ["slow", "timeout", "network", "session", "error"] as const;
export type SlowKind = (typeof SLOW_KINDS)[number];

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DIGITS = /^\d{3,}$/;
const HEX = /^[0-9a-f]{16,}$/i;
const TOKEN = /^(?=.*\d)[A-Za-z0-9_-]{20,}$/;

/**
 * "/api/pages/5f90a7c9-…/publish?x=1" → "/api/pages/:id/publish". Ids, long numbers,
 * hashes, tokens and anything holding an @ become ":id", so rows group by route and
 * nothing personal is kept. The query string and hash are dropped.
 */
export function routePattern(path: string): string {
  const bare = path.split(/[?#]/)[0] || "/";
  const parts = bare.split("/").map((seg) => {
    let s = seg;
    try {
      s = decodeURIComponent(seg);
    } catch {
      /* keep it as sent */
    }
    return UUID.test(s) || DIGITS.test(s) || HEX.test(s) || TOKEN.test(s) || s.includes("@") ? ":id" : seg;
  });
  return parts.join("/").slice(0, 200);
}

/** What the browser knows about one finished call. */
export interface CallOutcome {
  ms: number;
  /** The ApiClientError code when it failed ("timeout", "network_error", …), null when it answered. */
  errorCode: string | null;
  /** The HTTP status when one came back. */
  status: number | null;
  /** The caller set its own, longer ceiling (AI writing): slow by design, so only a timeout counts. */
  longByDesign: boolean;
  /** The caller cancelled it itself. */
  aborted: boolean;
  /** The tab was in the background, where browsers slow timers down. */
  hidden: boolean;
}

/** The kind to report for a call, or null when it is not worth a row. */
export function slowKindFor(o: CallOutcome): SlowKind | null {
  if (o.aborted) return null;
  if (o.errorCode === "timeout") return "timeout";
  if (o.errorCode === "session_unavailable") return "session";
  if (o.errorCode === "network_error" || (o.errorCode !== null && o.status === null)) return "network";
  if (o.longByDesign || o.hidden) return null;
  return o.ms >= SLOW_REQUEST_MS ? "slow" : null;
}
